import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import moment from "moment";
import ConfirmDialog from "../feedback/ConfirmDialog";
import {
  updatePaymentMethodSubscription,
  updatePaymentMethodSubscriptionDone,
  cancelPluginSubscription,
  setShowManagePluginModal,
} from "../plugins/plugin.redux";
import { paddleCheckout } from "../../utils/paddleCheckout";

/**
 * What a shop can do with an add-on it already pays for.
 *
 * Rebuilt to the house dialog: the navy accent, the separators and the
 * surfaceAlt card that every other modal in v3 has. It had been inline
 * styles with a hardcoded teal and a literal `red`, and an antd Select in a
 * react-bootstrap dialog.
 *
 * Two things that were not only cosmetic:
 *
 * The term selector defaulted to the plugin's first price rather than to the
 * one the shop is actually on. Both buttons act on the subscription matching
 * the selected price, so with the wrong term showing, Unsubscribe sent no
 * subscription id at all and did nothing -- silently, because the id is read
 * through optional chaining. It now offers only the terms actually held, and
 * stays out of the way when there is just one, which is the usual case.
 *
 * And unsubscribing happened on one tap of a red button. It is a paid
 * subscription: it asks first, and says what happens, like every other
 * destructive action in the app.
 */

const StyledModal = styled(Modal)`
  .modal-content {
    background-color: #ffffff;
    box-shadow: inset 0 4px 0 ${(p) => p.theme.color.primary},
      0 0.5rem 1rem rgba(0, 0, 0, 0.15);
    border-radius: ${(p) => p.theme.radius.md};
    border: none;
  }
  .modal-header {
    border-bottom: 1px solid ${(p) => p.theme.color.border};
    padding: calc(${(p) => p.theme.spacing.md} + 4px)
      ${(p) => p.theme.spacing.md} ${(p) => p.theme.spacing.md};
  }
  .modal-body {
    padding: ${(p) => p.theme.spacing.md};
    /* The term selector opens inside the body. */
    overflow: visible;
  }
  .modal-footer {
    border-top: 1px solid ${(p) => p.theme.color.border};
    padding: ${(p) => p.theme.spacing.sm} ${(p) => p.theme.spacing.md};
  }
`;

const ModalTitle = styled.span`
  font-family: ${(p) => p.theme.font.family};
  font-size: ${(p) => p.theme.font.sizeLg};
  font-weight: ${(p) => p.theme.font.weightSemibold};
  color: ${(p) => p.theme.color.primary};
  line-height: 1.2;
`;

const InfoCard = styled.div`
  background: ${(p) => p.theme.color.surfaceAlt};
  border-radius: ${(p) => p.theme.radius.md};
  padding: ${(p) => p.theme.spacing.md};
  text-align: center;
  font-family: ${(p) => p.theme.font.family};
  font-size: ${(p) => p.theme.font.sizeSm};
  color: ${(p) => p.theme.color.text};
`;

const PluginName = styled.div`
  font-size: ${(p) => p.theme.font.sizeMd};
  font-weight: ${(p) => p.theme.font.weightSemibold};
  color: ${(p) => p.theme.color.primary};
  margin-bottom: 2px;
`;

const Meta = styled.div`
  color: ${(p) => p.theme.color.textMuted};
`;

const Picker = styled.div`
  margin-top: ${(p) => p.theme.spacing.md};

  label {
    display: block;
    font-family: ${(p) => p.theme.font.family};
    font-size: ${(p) => p.theme.font.sizeSm};
    color: ${(p) => p.theme.color.textMuted};
    margin-bottom: 4px;
  }
`;

/* The footer's destructive action sits away from the two safe ones rather
   than beside them: Unsubscribe and Close are a bad pair to put a thumb
   between on a tablet. */
const FooterSplit = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${(p) => p.theme.spacing.sm};
  width: 100%;
  flex-wrap: wrap;
`;

const SafeActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${(p) => p.theme.spacing.sm};
  margin-left: auto;
`;

const ModalManagePluginRedux = (props) => {
  const { t } = useTranslation();
  const {
    showManagePluginModal,
    setShowManagePluginModal,
    pluginSubscriptions,
    updatePaymentMethodSubscription,
    updatePaymentMethodSubscriptionDone,
    pluginSubscriptionsData,
    cancelPluginSubscription,
    plugin,
    isSandbox,
  } = props;

  const [selectedPriceId, setSelectedPriceId] = useState("");
  const [confirmingCancel, setConfirmingCancel] = useState(false);

  const priceOptions = plugin?.prices || [];
  const meta = plugin?.metadata || plugin?.custom_data || {};
  const pluginName = meta.Key_Title ? t(meta.Key_Title) : plugin?.name || "";

  const subscriptionFor = (price) =>
    pluginSubscriptionsData.find(
      (sub) => sub.items?.[0]?.price?.id === price?.id,
    ) || null;

  // Only the terms this shop is actually paying for. The old list was every
  // price the plugin has, so it offered to manage a subscription that did
  // not exist.
  const heldPrices = priceOptions.filter((price) => subscriptionFor(price));

  useEffect(() => {
    if (!showManagePluginModal) return;
    setConfirmingCancel(false);
    setSelectedPriceId(heldPrices[0]?.id || "");
    // Re-seeded whenever the dialog opens, for whichever plugin it opened on.
  }, [showManagePluginModal, plugin?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedPrice =
    heldPrices.find((price) => price.id === selectedPriceId) || heldPrices[0];
  const subscription = subscriptionFor(selectedPrice);
  const subscribed =
    Boolean(subscription) &&
    pluginSubscriptions.indexOf(meta.pluginType) !== -1;

  const termLabel = (price) =>
    price?.billing_cycle?.interval
      ? t(`form.label.${price.billing_cycle.interval.toUpperCase()}`)
      : "";

  const priceLabel = selectedPrice?.unit_price
    ? `${selectedPrice.unit_price.currency_code} ${(
        Number(selectedPrice.unit_price.amount || 0) / 100
      ).toFixed(2)}`
    : "";

  // Already on its way out: Paddle cancels at the end of the period, so the
  // subscription stays `active` and carries a scheduled_change instead.
  const scheduledCancel =
    subscription?.scheduled_change?.action === "cancel"
      ? subscription.scheduled_change
      : null;

  // next_billed_at goes null the moment a cancellation is scheduled -- there
  // is no next bill. The date the shop cares about is still there, as the end
  // of the period they have paid for, which is what the plugin list falls
  // back to as well.
  const endDateRaw =
    subscription?.next_billed_at ||
    scheduledCancel?.effective_at ||
    subscription?.current_billing_period?.ends_at ||
    "";
  const endDate = endDateRaw ? moment(endDateRaw).format("DD/MM/YYYY") : "";
  const nextBilledAt = subscription?.next_billed_at ? endDate : "";

  const handleClose = () => setShowManagePluginModal(false);

  const handleUpdatePaymentMethod = () => {
    if (!subscribed) return;
    updatePaymentMethodSubscription();
    paddleCheckout({
      updatePaymentMethodSubscription: true,
      subscriptionId: subscription.id,
      isSandbox,
      onClose: () => updatePaymentMethodSubscriptionDone(),
    });
  };

  const handleUnsubscribe = () => {
    setConfirmingCancel(false);
    if (!subscribed) return;
    cancelPluginSubscription({ subscriptionId: subscription.id });
    handleClose();
  };

  return (
    <>
      <StyledModal
        show={showManagePluginModal && !confirmingCancel}
        onHide={handleClose}
        size="md"
        centered
      >
        <Modal.Header closeButton>
          <ModalTitle>{t("form.label.manage_plugin")}</ModalTitle>
        </Modal.Header>
        <Modal.Body>
          <InfoCard>
            <PluginName>{pluginName}</PluginName>
            <Meta>
              {[termLabel(selectedPrice), priceLabel]
                .filter(Boolean)
                .join(" · ")}
            </Meta>
            {endDate ? (
              <Meta>
                {t(
                  scheduledCancel
                    ? "form.label.ends_at"
                    : "form.label.next_billing",
                )}
                : {endDate}
              </Meta>
            ) : null}
            {scheduledCancel ? (
              <Meta>{t("managePlugin.alreadyCancelled")}</Meta>
            ) : null}
          </InfoCard>

          {/* Shown only when there is a choice to make. One subscription is
              the usual case, and its term is already on the card above. */}
          {heldPrices.length > 1 ? (
            <Picker>
              <label htmlFor="manage-plugin-term">
                {t("managePlugin.term")}
              </label>
              <Form.Select
                id="manage-plugin-term"
                value={selectedPrice?.id || ""}
                onChange={(e) => setSelectedPriceId(e.target.value)}
              >
                {heldPrices.map((price) => (
                  <option key={price.id} value={price.id}>
                    {termLabel(price)}
                  </option>
                ))}
              </Form.Select>
            </Picker>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <FooterSplit>
            {/* Nothing to offer on a subscription already on its way out;
                the card above says when it ends. */}
            <Button
              variant="danger"
              disabled={!subscribed || Boolean(scheduledCancel)}
              onClick={() => setConfirmingCancel(true)}
            >
              {t("form.label.cancel")}
            </Button>
            <SafeActions>
              <Button variant="light" onClick={handleClose}>
                {t("message.confirm.cancel")}
              </Button>
              <Button
                variant="primary"
                disabled={!subscribed}
                onClick={handleUpdatePaymentMethod}
              >
                {t("form.label.update_payment_method")}
              </Button>
            </SafeActions>
          </FooterSplit>
        </Modal.Footer>
      </StyledModal>

      {/* Replaces the dialog rather than stacking on it: a window over a
          window is what the rest of this app has been getting rid of. */}
      <ConfirmDialog
        show={confirmingCancel}
        onHide={() => setConfirmingCancel(false)}
        severity="danger"
        title={t("managePlugin.unsubscribeTitle", { plugin: pluginName })}
        description={t(
          nextBilledAt
            ? "managePlugin.unsubscribeBody"
            : "managePlugin.unsubscribeBodyNoDate",
          { plugin: pluginName, date: nextBilledAt },
        )}
        confirmText={t("form.label.cancel")}
        onConfirm={handleUnsubscribe}
      />
    </>
  );
};

export const ModalManagePlugin = connect(
  (state) => ({
    pluginSubscriptions: state.plugin.pluginSubscriptions,
    pluginSubscriptionsData: state.plugin.pluginSubscriptionsData,
    plugins: state.plugin.plugins,
    showManagePluginModal: state.plugin.showManagePluginModal,
    isSandbox: state.userSession.isSandbox,
  }),
  {
    updatePaymentMethodSubscription,
    updatePaymentMethodSubscriptionDone,
    cancelPluginSubscription,
    setShowManagePluginModal,
  },
)(ModalManagePluginRedux);
